import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Copy, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminResetLink() {
    const { user } = useAuth();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    if (user && user.role !== "admin") {
        return <Navigate to="/app" replace />;
    }

    const generate = async (e) => {
        e.preventDefault();
        setError("");
        setResult(null);
        if (!email.trim()) {
            setError("Enter the user's email address.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/admin/reset-link", { email: email.trim() });
            setResult(data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not generate a reset link.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(result.reset_url);
            toast.success("Reset link copied to clipboard");
        } catch {
            toast.error("Could not copy. Select the link manually");
        }
    };

    return (
        <div className="container-edit py-10 lg:py-14 max-w-2xl" data-testid="admin-reset-link-page">
            <Link
                to="/app/profile"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                data-testid="reset-link-back"
            >
                <ArrowLeft size={14} /> Back to profile
            </Link>
            <div className="mt-4 label-eyebrow">Admin</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                Password reset links
            </h1>
            <p className="mt-3 text-[#0D1B3E]/70 max-w-lg">
                Generate a secure, time-limited, single-use reset link for a user who
                has requested a password reset. Share it with them through a channel
                you trust. It expires after 30 minutes and can be used exactly once.
            </p>

            <form onSubmit={generate} className="mt-8 card-flat p-7">
                <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                        User's email address
                    </span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="field"
                        placeholder="user@lawfirm.eu"
                        data-testid="reset-link-email-input"
                    />
                </label>
                {error && (
                    <div
                        className="mt-4 text-sm border border-red-500/40 bg-red-50 text-red-900 px-3 py-2"
                        data-testid="reset-link-error"
                    >
                        {error}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary mt-6 disabled:opacity-60"
                    data-testid="reset-link-generate"
                >
                    {busy ? (
                        <><Loader2 size={15} className="animate-spin" /> Generating…</>
                    ) : (
                        <><KeyRound size={15} /> Generate reset link</>
                    )}
                </button>
            </form>

            {result && (
                <div className="mt-6 card-flat p-7 border-[#C9A84C]/50" data-testid="reset-link-result">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={15} color="#C9A84C" />
                        <span className="label-eyebrow">Link ready for {result.email}</span>
                    </div>
                    <div className="mt-4 bg-[#0D1B3E]/[0.04] border border-[#0D1B3E]/10 p-3 text-xs break-all font-mono" data-testid="reset-link-url">
                        {result.reset_url}
                    </div>
                    <div className="mt-3 text-xs text-[#0D1B3E]/60">
                        Valid for {result.ttl_min} minutes · single use · previous links for this user are now invalid.
                    </div>
                    <button
                        type="button"
                        onClick={copy}
                        className="btn-secondary mt-4 text-sm"
                        data-testid="reset-link-copy"
                    >
                        <Copy size={14} /> Copy link
                    </button>
                </div>
            )}
        </div>
    );
}
